// Map grids (docs/tapestry-design.md §3.7), in the image's own pixels. A square grid's offset is where two of its lines
// cross; a hex grid's is the middle of one hex. A hex's size is the distance between two opposite flat sides.

export type Grid = { kind: string; size: number; offsetX: number; offsetY: number };
type Area = { left: number; top: number; right: number; bottom: number };
type Hex = { cx: number; cy: number; corners: Array<[number, number]> };

/** At most this many cells are drawn; a grid finer than that isn't drawn at all. */
export const MAX_CELLS = 40_000;

/** The square grid's lines crossing the area, each as [x1, y1, x2, y2]. */
export function squareLines(grid: Grid, area: Area): Array<[number, number, number, number]> {
  const s = grid.size;
  if (!(s > 0) || ((area.right - area.left) / s) * ((area.bottom - area.top) / s) > MAX_CELLS) return [];
  const lines: Array<[number, number, number, number]> = [];
  for (let x = grid.offsetX + Math.ceil((area.left - grid.offsetX) / s) * s; x <= area.right; x += s) lines.push([x, area.top, x, area.bottom]);
  for (let y = grid.offsetY + Math.ceil((area.top - grid.offsetY) / s) * s; y <= area.bottom; y += s) lines.push([area.left, y, area.right, y]);
  return lines;
}

/** The hexes touching the area. */
export function hexCentres(grid: Grid, area: Area): Hex[] {
  const s = grid.size;
  if (!(s > 0)) return [];
  const r = s / Math.sqrt(3);
  const flat = grid.kind === "hex-flat";
  // Flat tops: columns 1.5 r apart, rows s apart, every other column half a row down. Pointy tops: the same, turned.
  const [stepX, stepY] = flat ? [1.5 * r, s] : [s, 1.5 * r];
  const c0 = Math.floor((area.left - grid.offsetX) / stepX) - 1;
  const c1 = Math.ceil((area.right - grid.offsetX) / stepX) + 1;
  const r0 = Math.floor((area.top - grid.offsetY) / stepY) - 1;
  const r1 = Math.ceil((area.bottom - grid.offsetY) / stepY) + 1;
  if ((c1 - c0 + 1) * (r1 - r0 + 1) > MAX_CELLS) return [];
  const angles = [0, 1, 2, 3, 4, 5].map((i) => ((flat ? 0 : 30) + 60 * i) * (Math.PI / 180));
  const hexes: Hex[] = [];
  for (let col = c0; col <= c1; col++)
    for (let row = r0; row <= r1; row++) {
      const [cx, cy] = hexCentre(grid, col, row);
      hexes.push({ cx, cy, corners: angles.map((a): [number, number] => [cx + r * Math.cos(a), cy + r * Math.sin(a)]) });
    }
  return hexes;
}

function hexCentre(grid: Grid, col: number, row: number): [number, number] {
  const s = grid.size;
  const r = s / Math.sqrt(3);
  if (grid.kind === "hex-flat") return [grid.offsetX + col * 1.5 * r, grid.offsetY + row * s + (odd(col) ? s / 2 : 0)];
  return [grid.offsetX + col * s + (odd(row) ? s / 2 : 0), grid.offsetY + row * 1.5 * r];
}

function odd(n: number): boolean {
  return ((n % 2) + 2) % 2 === 1;
}

/** The middle of the cell the point is in. */
export function snapToGrid(grid: Grid, x: number, y: number): [number, number] {
  const s = grid.size;
  if (!(s > 0)) return [x, y];
  if (grid.kind === "square")
    return [grid.offsetX + (Math.floor((x - grid.offsetX) / s) + 0.5) * s, grid.offsetY + (Math.floor((y - grid.offsetY) / s) + 0.5) * s];
  if (grid.kind !== "hex-flat" && grid.kind !== "hex-pointy") return [x, y];
  // The nearest middle among the hexes around the point's rough column and row.
  const r = s / Math.sqrt(3);
  const [stepX, stepY] = grid.kind === "hex-flat" ? [1.5 * r, s] : [s, 1.5 * r];
  const col = Math.round((x - grid.offsetX) / stepX);
  const row = Math.round((y - grid.offsetY) / stepY);
  let best: [number, number] = [x, y];
  let bestDistance = Infinity;
  for (let c = col - 1; c <= col + 1; c++)
    for (let w = row - 1; w <= row + 1; w++) {
      const [cx, cy] = hexCentre(grid, c, w);
      const distance = (cx - x) ** 2 + (cy - y) ** 2;
      if (distance < bestDistance) [best, bestDistance] = [[cx, cy], distance];
    }
  return best;
}
